import { IconProps, Portal } from '@chakra-ui/react'
import {
  ComponentType,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { CustomTooltipContainer } from '~/components/shared/CustomTooltip/CustomTooltipContainer'
import {
  QuickActionButton,
  QuickActionButtonSizes,
} from '~/components/shared/QuickActionButton'
import { Popover, PopoverAnchor } from '~/components/ui/popover'

type PopoverActionButtonProps = {
  variant: QuickActionButtonVariants
  size?: QuickActionButtonSizes
  tooltipText: string
  ariaLabel?: string
  icon: ComponentType<any>
  onButtonClick?: () => void
  iconProps?: IconProps
  popoverContents?: ReactNode
  disabled?: boolean
}

export const CustomTooltipActionButton = ({
  variant,
  size,
  tooltipText,
  ariaLabel,
  icon,
  onButtonClick,
  iconProps,
  popoverContents,
}: PopoverActionButtonProps) => {
  const [menuOpen, setMenuOpen] = useState(false)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    onButtonClick?.()
  }

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const handleMouseEnter = () => {
    if (!popoverContents) return
    clearCloseTimeout()
    setMenuOpen(true)
  }

  const handleMouseLeave = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = setTimeout(() => {
      setMenuOpen(false)
    }, 100)
  }

  return (
    <Popover open={menuOpen} positioning={{ placement: 'bottom-start' }}>
      <PopoverAnchor>
        <QuickActionButton
          ref={buttonRef}
          actionIcon={icon}
          tooltipText={tooltipText}
          ariaLabel={ariaLabel}
          tooltipDisabled={menuOpen}
          variant={variant}
          size={size}
          onClick={handleButtonClick}
          iconProps={iconProps}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      </PopoverAnchor>
      <Portal>
        {menuOpen && (
          <CustomTooltipContainer
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {popoverContents}
          </CustomTooltipContainer>
        )}
      </Portal>
    </Popover>
  )
}
